import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { CardSection, CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import { useState } from 'react';
import { PolymorphicLink } from '../PolymorphicLink';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import FormattedDuration from 'I18n/components/FormattedDuration';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { ContentTypeBadgeMap, ClampedText } from './shared';
import { stringToHash } from '../../utils/math';
import ACDynamicThumbnailImage from './ACDynamicThumbnailImage';
import { View } from 'foundations-components/components/View';
import { Box } from 'foundations-components/transitional/components/Box';
import { useMediaQuery } from '../../utils/Styling';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import { useTheme } from 'foundations-theming/utils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function ACContentCardSmall({
  imageSrc,
  name,
  description,
  contentType,
  lengthType,
  length,
  progress,
  awardLink,
  bordered = false,
  linkMode = 'default',
  contentHref,
  cardMenu,
  onClick,
  ribbonText,
  ribbonUse,
  thumbnailOverlay,
  use: useProp = 'default',
  size = 60
}) {
  const use = bordered || ribbonText ? 'default' : useProp;
  const [isHovering, setIsHovering] = useState(false);
  const {
    isMobile
  } = useMediaQuery();
  const theme = useTheme();
  const content = /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    "data-test-id": "academy-content-card-small",
    inlineSize: "100%",
    onClick: onClick,
    onMouseEnter: () => setIsHovering(true),
    onMouseLeave: () => setIsHovering(false),
    position: "relative",
    children: [/*#__PURE__*/_jsx(Box, {
      grow: 1,
      children: /*#__PURE__*/_jsx(PolymorphicLink, {
        href: contentHref,
        linkMode: linkMode,
        use: "unstyled",
        children: /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          gap: "400",
          children: [/*#__PURE__*/_jsxs(View, {
            minBlockSize: `${size}px`,
            minInlineSize: `${size}px`,
            position: "relative",
            children: [imageSrc ? /*#__PURE__*/_jsx(Image, {
              alt: name,
              blockSize: `${size}px`,
              inlineSize: `${size}px`,
              loading: 'lazy',
              objectFit: "cover",
              src: imageSrc
            }) : /*#__PURE__*/_jsx(View, {
              maxBlockSize: `${size}px`,
              maxInlineSize: `${size}px`,
              minBlockSize: `${size}px`,
              minInlineSize: `${size}px`,
              children: /*#__PURE__*/_jsx(ACDynamicThumbnailImage, {
                seed: stringToHash(name + contentType + contentHref),
                title: name,
                type: "square"
              })
            }), /*#__PURE__*/_jsx(View, {
              blockSize: "6px",
              inlineSize: `${size}px`,
              insetBlockEnd: "0",
              insetInlineStart: "0",
              position: "absolute",
              children: /*#__PURE__*/_jsx(NanoProgress, {
                color: theme.color['fill-accent-teal-default'],
                height: 6,
                value: (progress !== null && progress !== void 0 ? progress : 0) / length * 100
              })
            }), /*#__PURE__*/_jsx(View, {
              inset: "0",
              position: "absolute",
              children: thumbnailOverlay
            })]
          }), /*#__PURE__*/_jsxs(Flex, {
            flexDirection: "column",
            gap: "200",
            justifyContent: "space-between",
            maxBlockSize: `${size}px`,
            children: [/*#__PURE__*/_jsx(Tooltip, {
              delay: 500,
              title: name,
              children: /*#__PURE__*/_jsx(Text, {
                as: "h6",
                use: use,
                children: /*#__PURE__*/_jsx(ClampedText, {
                  underlineOnHover: true,
                  children: name
                })
              })
            }), description && /*#__PURE__*/_jsx(Text, {
              children: /*#__PURE__*/_jsx(ClampedText, {
                children: description
              })
            }), /*#__PURE__*/_jsx(Text, {
              as: "small",
              marginBlockStart: "50",
              use: use === 'default' ? 'help' : 'on-dark',
              children: !progress ? /*#__PURE__*/_jsxs(List, {
                use: "inline-divided",
                children: [contentType && /*#__PURE__*/_jsx(ListItem, {
                  children: ContentTypeBadgeMap[contentType]
                }), !!length && /*#__PURE__*/_jsx(ListItem, {
                  children: lengthType === 'items' ? /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.contentCard.itemCount",
                    options: {
                      count: length
                    }
                  }) : /*#__PURE__*/_jsx(FormattedDuration, {
                    from: Date.now() - length * 1000
                  })
                })]
              }) : progress === length ? /*#__PURE__*/_jsxs(Flex, {
                flexWrap: "wrap",
                gap: "200",
                justifyContent: "space-between",
                children: [/*#__PURE__*/_jsxs(Flex, {
                  alignItems: "center",
                  gap: "100",
                  children: [/*#__PURE__*/_jsx(SuccessIcon, {
                    color: "fill-positive-default"
                  }), /*#__PURE__*/_jsx(Text, {
                    as: "small",
                    children: ['VIDEO', 'ACADEMY_VIDEO'].includes(contentType !== null && contentType !== void 0 ? contentType : '') ? /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "academyCommon.progress.watched"
                    }) : /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "academyCommon.progress.completed"
                    })
                  })]
                }), awardLink]
              }) : /*#__PURE__*/_jsxs(List, {
                use: "inline-divided",
                children: [contentType && /*#__PURE__*/_jsx(ListItem, {
                  children: ContentTypeBadgeMap[contentType]
                }), !!length && /*#__PURE__*/_jsxs(ListItem, {
                  children: [/*#__PURE__*/_jsx(StatusDot, {
                    use: "active"
                  }), lengthType === 'items' ? /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.contentCard.itemsRemaining",
                    options: {
                      count: length - progress
                    }
                  }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
                    message: "academyCommon.tracks.timeRemaining.duration",
                    options: {
                      duration: /*#__PURE__*/_jsx(FormattedDuration, {
                        from: Date.now() - (length - progress) * 1000
                      })
                    }
                  })]
                })]
              })
            })]
          })]
        })
      })
    }), cardMenu && /*#__PURE__*/_jsx(Flex, {
      alignItems: "center",
      blockSize: "100%",
      marginInline: "400",
      visibility: isHovering || isMobile ? 'visible' : 'hidden',
      children: cardMenu
    })]
  });
  return bordered || ribbonText ? /*#__PURE__*/_jsxs(CardWrapper, {
    compact: true,
    inlineSize: "100%",
    position: "relative",
    children: [ribbonText && /*#__PURE__*/_jsx(Ribbon, {
      position: "right",
      use: ribbonUse,
      children: ribbonText
    }), /*#__PURE__*/_jsx(CardSection, {
      children: content
    })]
  }) : content;
}